// Hlavička podle prototypu: logo + nápis ECOser App (odkaz na Domů), název sekce, jedno pole pro hledání v celé
// aplikaci a pro asistenta (klávesa „/“ i Ctrl+K, kroky 12.6 a 3.8, rozhodnutí 05/1520 — Hledani.tsx; panel asistenta
// se stahuje až při prvním otevření — asistent/AsistentVHlavicce.tsx), nápověda „?“ (F1, krok 12.5
// — NapovedaTlacitko.tsx), ⚑ Nahlásit, zvonek notifikací
// (krok 4.3 — Notifikace.tsx), tlačítka ← → (historie aplikace — HistorieTlacitka.tsx), malá živá váha pro toho,
// kdo smí vážit (VahaVHlavicce.tsx), uživatel (iniciály, jméno, role) s nabídkou: světlý/tmavý vzhled, Můj profil
// a nastavení, Odhlásit se. Pod hlavičkou proužek náhledu za uživatele (Ukončit).
// Dotykový tablet na výšku se spodní lištou (rozhodnutí zadavatele 30. 10. 2026 „Uklidit, Nahlásit nechat“, W86):
// ← · logo · váha · hledání · ⚑ (ikona) · zvonek · iniciály — ☰ je ve spodní liště (Menu, Shell.css) a nápověda
// v nabídce pod iniciálami.
import type { Ja } from '@ecoser/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { naChybu, type Chyba } from '../api/klient';
import { obnovitPoZmeneIdentity, ukoncitNahled } from '../api/nahled';
import { AsistentVHlavicce } from '../asistent/AsistentVHlavicce';
import { TlacitkoSDuvodem } from '../komponenty/TlacitkoSDuvodem';
import { useToast } from '../komponenty/Toast';
import { popisekRole } from '../prihlaseni/nazvy';
import { usePrihlaseni } from '../prihlaseni/stav-prihlaseni';
import { HistorieTlacitka } from './HistorieTlacitka';
import { Ikona } from './ikony';
import { HledaniVHlavicce } from './Hledani';
import { useNastaveni } from './nastaveni-uzivatele';
import { NahlasitTlacitko } from './NahlasitTlacitko';
import { NapovedaTlacitko } from './NapovedaTlacitko';
import { Notifikace } from './Notifikace';
import { useJeTabletSeSpodniListou } from './telefon';
import { VahaVHlavicce } from './VahaVHlavicce';
import './Shell.css';

function celeJmeno(ja: Ja): string {
  const z = ja.zamestnanec;
  return z ? `${z.jmeno} ${z.prijmeni}`.trim() : '';
}

function inicialy(jmeno: string): string {
  return (
    jmeno
      .split(/\s+/)
      .filter(Boolean)
      .map((cast) => cast.charAt(0))
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

function PrepinacVzhledu() {
  const { nastaveni, upravit } = useNastaveni();
  const toast = useToast();

  const nastavit = (theme: 'light' | 'dark') => {
    if (theme === nastaveni.theme) return;
    const puvodni = nastaveni.theme;
    upravit({ theme });
    toast.ukazat(theme === 'dark' ? 'Tmavý režim zapnut' : 'Světlý režim zapnut', {
      zpet: () => upravit({ theme: puvodni }),
    });
  };

  return (
    <div role="group" aria-labelledby="vzhled-popisek">
      <span id="vzhled-popisek" className="segment__popisek">
        Vzhled
      </span>
      <div className="segment">
        <button
          type="button"
          className="segment__tlacitko"
          aria-pressed={nastaveni.theme === 'light'}
          onClick={() => nastavit('light')}
        >
          <Ikona id="sun" velikost={14} /> Světlý
        </button>
        <button
          type="button"
          className="segment__tlacitko"
          aria-pressed={nastaveni.theme === 'dark'}
          onClick={() => nastavit('dark')}
        >
          <Ikona id="moon" velikost={14} /> Tmavý
        </button>
      </div>
    </div>
  );
}

function NabidkaUzivatele({ ja, naNapovedu }: { ja: Ja; naNapovedu?: () => void }) {
  const { odhlasit } = usePrihlaseni();
  const [otevrena, setOtevrena] = useState(false);
  const [odhlasuji, setOdhlasuji] = useState(false);
  const [chyba, setChyba] = useState<Chyba | null>(null);
  const jmeno = celeJmeno(ja);
  const role = popisekRole(ja.role_efektivni || ja.role) ?? 'bez role';

  useEffect(() => {
    if (!otevrena) return undefined;
    const naKlavesu = (udalost: KeyboardEvent) => {
      if (udalost.key === 'Escape') setOtevrena(false);
    };
    document.addEventListener('keydown', naKlavesu);
    return () => document.removeEventListener('keydown', naKlavesu);
  }, [otevrena]);

  const naOdhlasit = async () => {
    setOdhlasuji(true);
    setChyba(null);
    try {
      await odhlasit();
    } catch (e) {
      setChyba(naChybu(e));
      setOdhlasuji(false);
    }
  };

  return (
    <div className="uzivatel">
      <button
        type="button"
        className="uzivatel__tlacitko"
        aria-haspopup="true"
        aria-expanded={otevrena}
        aria-controls={otevrena ? 'uzivatel-nabidka' : undefined}
        title="Můj profil — vzhled, nastavení, odhlášení"
        onClick={() => setOtevrena((o) => !o)}
      >
        <span className="uzivatel__inicialy" aria-hidden="true">
          {inicialy(jmeno)}
        </span>
        <span className="uzivatel__popis">
          <span className="uzivatel__jmeno">{jmeno}</span>
          <span className="uzivatel__role">{role}</span>
        </span>
        <span className="uzivatel__sipka" aria-hidden="true" />
      </button>
      {otevrena && (
        <>
          <button
            type="button"
            className="prekryv uzivatel__podklad"
            aria-label="Zavřít nabídku"
            tabIndex={-1}
            onClick={() => setOtevrena(false)}
          />
          <div
            id="uzivatel-nabidka"
            className="uzivatel__nabidka"
            role="group"
            aria-label="Nabídka uživatele"
          >
            <p className="uzivatel__nabidka-jmeno">{jmeno}</p>
            <p className="uzivatel__nabidka-role">{role}</p>
            <PrepinacVzhledu />
            {naNapovedu && (
              <button
                type="button"
                className="tlacitko tlacitko--sekundarni"
                aria-haspopup="dialog"
                onClick={() => {
                  setOtevrena(false);
                  naNapovedu();
                }}
                data-testid="napoveda"
              >
                <span aria-hidden="true">?</span> Nápověda
              </button>
            )}
            <Link
              to="/nastaveni"
              className="tlacitko tlacitko--sekundarni"
              onClick={() => setOtevrena(false)}
            >
              Můj profil a nastavení
            </Link>
            <button
              type="button"
              className="tlacitko tlacitko--sekundarni"
              onClick={() => void naOdhlasit()}
              disabled={odhlasuji}
            >
              {odhlasuji ? 'Odhlašuji…' : 'Odhlásit se'}
            </button>
            {chyba && (
              <p className="pole__chyba uzivatel__chyba" role="alert">
                <strong>Odhlášení se nepovedlo.</strong> {chyba.zprava}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface Props {
  ja: Ja;
  /** Název aktuální sekce (h1 zůstává v obsahu; tady jen orientace). */
  nazevSekce?: string;
  menuOtevrene: boolean;
  naMenu: () => void;
}

export function Hlavicka({ ja, nazevSekce, menuOtevrene, naMenu }: Props) {
  const [napoveda, setNapoveda] = useState(false);
  const tabletSeSpodniListou = useJeTabletSeSpodniListou();
  return (
    <header className="hlavicka">
      <div className="hlavicka__vlevo">
        <button
          type="button"
          className="hlavicka__menu"
          aria-label={menuOtevrene ? 'Zavřít menu' : 'Otevřít menu'}
          aria-expanded={menuOtevrene}
          onClick={naMenu}
        >
          <Ikona id={menuOtevrene ? 'x' : 'menu'} velikost={18} />
        </button>
        <HistorieTlacitka />
        {/* Jediná cesta na Domů (v liště položka Domů není, stejně jako v prototypu) —
            proto aria-label, ať čtečka obrazovky řekne, kam odkaz vede. */}
        <Link to="/" className="hlavicka__logo" title="Domů" aria-label="Domů — ECOser App">
          <img
            src="/brand/ecoservis-logo-dark-text.svg"
            alt="ECOSERVIS"
            className="hlavicka__logo-obrazek hlavicka__logo-obrazek--svetly"
          />
          <img
            src="/brand/ecoservis-logo-white-text.svg"
            alt=""
            aria-hidden="true"
            className="hlavicka__logo-obrazek hlavicka__logo-obrazek--tmavy"
          />
          <span className="napis-app">
            ECO<span className="napis-app__ser">ser</span>
            <span className="napis-app__app"> App</span>
          </span>
        </Link>
        {nazevSekce && <span className="hlavicka__sekce">{nazevSekce}</span>}
      </div>
      <div className="hlavicka__vpravo">
        <VahaVHlavicce ja={ja} />
        <HledaniVHlavicce ja={ja} />
        <AsistentVHlavicce ja={ja} />
        <NapovedaTlacitko
          ja={ja}
          otevreno={napoveda}
          naOtevreno={setNapoveda}
          sTlacitkem={!tabletSeSpodniListou}
        />
        <NahlasitTlacitko ja={ja} />
        <Notifikace ja={ja} />
        <NabidkaUzivatele
          ja={ja}
          naNapovedu={tabletSeSpodniListou ? () => setNapoveda(true) : undefined}
        />
      </div>
    </header>
  );
}

/** Proužek „Náhled za uživatele“ — jen když /ja vrací `nahled_za`; Ukončit = POST /nahled/stop → znovu /ja. */
export function NahledPruh({ ja }: { ja: Ja }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [ukoncuji, setUkoncuji] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);
  if (!ja.nahled_za) return null;

  const naUkoncit = async () => {
    setUkoncuji(true);
    setChyba(null);
    try {
      await ukoncitNahled();
      await obnovitPoZmeneIdentity(queryClient);
      toast.ukazat('Náhled za uživatele ukončen');
    } catch (e) {
      setChyba(naChybu(e).zprava);
    } finally {
      setUkoncuji(false);
    }
  };

  return (
    <div className="nahled-pruh" role="status" data-testid="nahled-pruh">
      <Ikona id="eye" velikost={16} />
      <span>
        Náhled za uživatele: <strong>{celeJmeno(ja)}</strong> (
        {popisekRole(ja.role_efektivni || ja.role) ?? 'bez role'})
      </span>
      <TlacitkoSDuvodem
        varianta="sekundarni"
        vRadku
        disabled={ukoncuji}
        duvod="Ukončuji…"
        onClick={() => void naUkoncit()}
      >
        Ukončit náhled
      </TlacitkoSDuvodem>
      {chyba && (
        <span className="pole__chyba" role="alert">
          Ukončení se nepovedlo. {chyba}
        </span>
      )}
    </div>
  );
}
